import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, XIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { projects } from '../data/projects';
export function ProjectGallery() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const project = projects.find(p => p.slug === slug);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('portfolio');
      if (el) {
        el.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }, 100);
  };
  const openLightbox = (index: number) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const goNext = () => {
    if (project && lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex + 1) % project.images.length);
    }
  };
  const goPrev = () => {
    if (project && lightboxIndex !== null) {
      setLightboxIndex((lightboxIndex - 1 + project.images.length) % project.images.length);
    }
  };
  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (lightboxIndex === null) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [lightboxIndex]);
  if (!project) {
    return <div className="min-h-screen bg-[#F5F3F0] flex items-center justify-center" data-id="element-343">
        <div className="text-center" data-id="element-344">
          <h1 className="text-4xl font-serif font-bold text-[#1A1A1A] mb-4" data-id="element-345">
            Project Not Found
          </h1>
          <a href="/" onClick={handleBack} className="text-[#A07656] hover:text-[#B8896A] transition-colors font-semibold" data-id="element-346">
            ← Back to Portfolio
          </a>
        </div>
      </div>;
  }
  return <div className="min-h-screen bg-[#1A1A1A]" data-id="element-347">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 px-6 py-5 flex justify-between items-center bg-[#1A1A1A]/95 backdrop-blur-md border-b border-[#252525]" data-id="element-348">
        <a href="/" onClick={handleBack} data-id="element-349">
          <img src="https://cdn.magicpatterns.com/uploads/rsVSuidRepyz9oxNYVg3rK/Iron_Ridge_Logo.png" alt="Iron Ridge Custom Homes" className="h-12 md:h-14 w-auto brightness-[10] contrast-[0.9]" data-id="element-350" />
        </a>
        <a href="/" onClick={handleBack} className="flex items-center gap-2 text-sm uppercase tracking-widest font-medium text-[#F5F3F0] hover:text-[#A07656] transition-colors" data-id="element-351">
          <ArrowLeft className="w-4 h-4" data-id="element-352" />
          Back
        </a>
      </header>

      {/* Hero */}
      <div className="relative h-[60vh] min-h-[450px] overflow-hidden" data-id="element-353">
        <img src={project.image} alt={project.title} className="w-full h-full object-cover" data-id="element-354" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-[#1A1A1A]/40 to-transparent" data-id="element-355" />
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16 lg:p-20" data-id="element-356">
          <div className="max-w-5xl mx-auto" data-id="element-357">
            <motion.span initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-[#A07656] text-xs font-bold uppercase tracking-[0.2em] mb-3 block" data-id="element-358">
              {project.type} · {project.location}
            </motion.span>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white leading-[1.1] mb-6" data-id="element-359">
              {project.title}
            </motion.h1>
            <motion.div initial={{
            opacity: 0,
            y: 15
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="flex flex-wrap gap-6 text-sm" data-id="element-360">
              <div data-id="element-361">
                <span className="text-[#D4CFC8]/50 uppercase tracking-wider text-xs block mb-1" data-id="element-362">
                  Year
                </span>
                <span className="text-[#F5F3F0] font-semibold" data-id="element-363">
                  {project.year}
                </span>
              </div>
              <div data-id="element-364">
                <span className="text-[#D4CFC8]/50 uppercase tracking-wider text-xs block mb-1" data-id="element-365">
                  Size
                </span>
                <span className="text-[#F5F3F0] font-semibold" data-id="element-366">
                  {project.sqft} SF
                </span>
              </div>
              <div data-id="element-367">
                <span className="text-[#D4CFC8]/50 uppercase tracking-wider text-xs block mb-1" data-id="element-368">
                  Value
                </span>
                <span className="text-[#F5F3F0] font-semibold" data-id="element-369">
                  {project.value}
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Project Description */}
      <div className="max-w-5xl mx-auto px-6 md:px-8 py-12 md:py-16" data-id="element-370">
        <p className="text-lg md:text-xl text-[#D4CFC8]/70 leading-relaxed max-w-3xl" data-id="element-371">
          {project.description}
        </p>
      </div>

      {/* Photo Gallery Grid */}
      <div className="max-w-6xl mx-auto px-6 md:px-8 pb-20" data-id="element-372">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-373">
          {project.images.map((img, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className={`relative overflow-hidden cursor-pointer group ${index === 0 ? 'md:col-span-2 aspect-[16/9]' : 'aspect-[4/3]'}`} onClick={() => openLightbox(index)} data-id="element-374">
              <img src={img.src} alt={img.alt} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-375" />
              <div className="absolute inset-0 bg-[#1A1A1A]/0 group-hover:bg-[#1A1A1A]/20 transition-colors duration-300" data-id="element-376" />
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-377">
                <span className="text-white text-sm uppercase tracking-widest font-semibold bg-[#1A1A1A]/60 backdrop-blur-sm px-4 py-2" data-id="element-378">
                  View
                </span>
              </div>
            </motion.div>)}
        </div>
      </div>

      {/* Back link */}
      <div className="max-w-5xl mx-auto px-6 md:px-8 pb-20" data-id="element-379">
        <div className="pt-10 border-t border-[#252525]" data-id="element-380">
          <a href="/" onClick={handleBack} className="inline-flex items-center gap-2 text-[#A07656] hover:text-[#B8896A] transition-colors font-semibold text-sm uppercase tracking-[0.15em]" data-id="element-381">
            <ArrowLeft className="w-4 h-4" data-id="element-382" />
            Back to Portfolio
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-[#1A1A1A] border-t border-[#252525] py-8 text-center" data-id="element-383">
        <p className="text-xs text-[#D4CFC8]/40" data-id="element-384">
          &copy; {new Date().getFullYear()} Iron Ridge Custom Homes. All rights
          reserved.
        </p>
      </div>

      {/* Lightbox */}
      <AnimatePresence data-id="element-385">
        {lightboxIndex !== null && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 bg-[#0A0A0A]/95 flex items-center justify-center" onClick={closeLightbox} data-id="element-386">
            {/* Close button */}
            <button onClick={closeLightbox} className="absolute top-6 right-6 text-white/60 hover:text-white transition-colors z-10" data-id="element-387">
              <XIcon className="w-8 h-8" data-id="element-388" />
            </button>

            {/* Counter */}
            <div className="absolute top-6 left-6 text-white/40 text-sm font-medium" data-id="element-389">
              {lightboxIndex + 1} / {project.images.length}
            </div>

            {/* Nav buttons */}
            <button onClick={e => {
          e.stopPropagation();
          goPrev();
        }} className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/50 hover:text-white transition-colors" data-id="element-390">
              <ChevronLeftIcon className="w-8 h-8" data-id="element-391" />
            </button>
            <button onClick={e => {
          e.stopPropagation();
          goNext();
        }} className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/50 hover:text-white transition-colors" data-id="element-392">
              <ChevronRightIcon className="w-8 h-8" data-id="element-393" />
            </button>

            {/* Image */}
            <motion.img key={lightboxIndex} initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.3
        }} src={project.images[lightboxIndex].src} alt={project.images[lightboxIndex].alt} className="max-w-[90vw] max-h-[85vh] object-contain" onClick={e => e.stopPropagation()} data-id="element-394" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}